import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Screen } from "@/components/layout/Screen";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { LoadingState } from "@/components/interactions/LoadingState";
import { EmptyState } from "@/components/interactions/EmptyState";
import { getAuthenticatedClient, getCurrentUserId } from "@/services/client";
import { edgeFunctions } from "@/services/edgeFunctions";
import { useSimpleMode } from "@/contexts/SimpleModeContext";
import { SimpleMetric, SimpleMetricGrid } from "@/components/cbs/SimpleMetric";
import { cn } from "@/lib/utils";
import {
  Bird,
  Syringe, ClipboardList, DollarSign, ExternalLink,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────

interface BatchRow {
  id: string;
  batch_code: string | null;
  batch_status: string;
  placement_date: string;
  planned_cycle_days: number;
  doc_count: number;
  cumulative_mortality: number | null;
  cumulative_feed_kg: number | null;
  current_fcr: number | null;
  current_bird_count: number | null;
  latest_avg_weight_kg: number | null;
  breed_id: string;
  breeds?: { name: string } | null;
}

interface DailyRecordRow {
  id: string;
  record_date: string;
  batch_day: number;
  feed_consumed_kg: number | null;
  mortality_count: number;
  temperature_min_c: number | null;
  temperature_max_c: number | null;
  running_fcr: number | null;
  running_bird_count: number | null;
  running_cumulative_feed_kg: number | null;
  weight_sample_avg_g: number | null;
  weight_sample_count: number | null;
  mortality_cause: string | null;
}

interface VaccinationRow {
  id: string;
  vaccination_date: string;
  vaccine_name: string;
  administration_route: string | null;
  batch_day: number | null;
}

// ── Status badge helper ────────────────────────────────

const statusVariant: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  placed: "secondary",
  active: "default",
  harvesting: "outline",
  completed: "secondary",
  abandoned: "destructive",
};

// ── FCR color helper ───────────────────────────────────

const getFcrColor = (fcr: number): 'success' | 'warning' | 'destructive' => {
  if (fcr <= 1.6) return 'success';
  if (fcr <= 1.8) return 'warning';
  return 'destructive';
};

// ── Component ──────────────────────────────────────────

export const BatchDetailScreen: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSimpleMode } = useSimpleMode();

  // ── Batch query ──────────────────────────────────────
  const { data: batch, isLoading: batchLoading } = useQuery<BatchRow | null>({
    queryKey: ["cbs", "batch-detail", batchId],
    queryFn: async () => {
      if (!batchId) return null;
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from("batches")
        .select("id, batch_code, batch_status, placement_date, planned_cycle_days, doc_count, cumulative_mortality, cumulative_feed_kg, current_fcr, current_bird_count, latest_avg_weight_kg, breed_id, breeds(name)")
        .eq("id", batchId)
        .maybeSingle();
      if (error) throw error;
      return data as BatchRow | null;
    },
    enabled: !!batchId,
  });

  // ── Daily records query ──────────────────────────────
  const { data: dailyRecords = [] } = useQuery<DailyRecordRow[]>({
    queryKey: ["cbs", "daily-records", batchId],
    queryFn: async () => {
      if (!batchId) return [];
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from("daily_records")
        .select("id, record_date, batch_day, feed_consumed_kg, mortality_count, temperature_min_c, temperature_max_c, running_fcr, running_bird_count, running_cumulative_feed_kg, weight_sample_avg_g, weight_sample_count, mortality_cause")
        .eq("batch_id", batchId)
        .order("batch_day", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as DailyRecordRow[];
    },
    enabled: !!batchId,
  });

  // ── Vaccination records query ────────────────────────
  const { data: vaccinations = [] } = useQuery<VaccinationRow[]>({
    queryKey: ["cbs", "vaccinations", batchId],
    queryFn: async () => {
      if (!batchId) return [];
      const client = getAuthenticatedClient();
      const { data, error } = await (client as any)
        .from("vaccination_records")
        .select("id, vaccination_date, vaccine_name, administration_route, batch_day")
        .eq("batch_id", batchId)
        .order("vaccination_date", { ascending: true });
      if (error) return [];
      return (data ?? []) as unknown as VaccinationRow[];
    },
    enabled: !!batchId,
  });

  // ── Settlement preview ───────────────────────────────
  const showSettlement = batch?.batch_status === "harvesting" || batch?.batch_status === "completed";
  const { data: settlement } = useQuery({
    queryKey: ["cbs", "settlement-preview", batchId],
    queryFn: async () => {
      if (!batchId) return null;
      const { data, error } = await edgeFunctions.calculateSettlement({
        batchId,
        harvestData: {
          totalBirds: batch?.current_bird_count ?? 0,
          avgWeightKg: batch?.latest_avg_weight_kg ?? 0,
          pricePerKg: 0,
        },
      });
      if (error) return null;
      return data;
    },
    enabled: !!batchId && showSettlement,
  });

  // ── Derived data ─────────────────────────────────────
  const currentDay = useMemo(() => {
    if (!batch) return 0;
    const placed = new Date(batch.placement_date);
    const now = new Date();
    return Math.max(1, Math.floor((now.getTime() - placed.getTime()) / 86400000) + 1);
  }, [batch]);

  const progressPct = batch ? Math.min(100, (currentDay / batch.planned_cycle_days) * 100) : 0;


  // ── Loading / empty states ───────────────────────────
  if (batchLoading) {
    return (
      <Screen showTopBar showBottomNav={false}>
        <LoadingState message={t("cbs.batchDetail.loading", { defaultValue: "Loading batch..." })} />
      </Screen>
    );
  }

  if (!batch) {
    return (
      <Screen showTopBar showBottomNav={false}>
        <EmptyState
          icon={Bird}
          title={t("cbs.batchDetail.notFound", { defaultValue: "Batch not found" })}
          description={t("cbs.batchDetail.notFoundDesc", { defaultValue: "This batch does not exist or you don't have access." })}
          action={{ label: t("common.back"), onClick: () => navigate(-1) }}
        />
      </Screen>
    );
  }

  const breedName = (batch as any).breeds?.name ?? t("cbs.batchDetail.unknownBreed", { defaultValue: "Unknown Breed" });
  const mortPct = batch.doc_count > 0 ? (((batch.cumulative_mortality ?? 0) / batch.doc_count) * 100) : 0;

  return (
    <Screen
      showTopBar
      showBottomNav={false}
      topBarProps={{
        title: isSimpleMode ? `🐔 ${batch.batch_code || t("cbs.batch.title", { defaultValue: "Batch" })}` : (batch.batch_code || t("cbs.batchDetail.title", { defaultValue: "Batch Detail" })),
        showBack: true,
        onBack: () => navigate(-1),
      }}
    >
      <div className="p-4 space-y-6 pb-8">
        {/* ── SIMPLE MODE HEADER ────────────────────── */}
        {isSimpleMode ? (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="p-4 space-y-4">
              {/* Day progress */}
              <SimpleMetric emoji="📅" value={`${t("cbs.batchDetail.day", { defaultValue: "Day" })} ${Math.min(currentDay, batch.planned_cycle_days)}`} unit={`/ ${batch.planned_cycle_days}`} size="lg" color="info" />
              <Progress value={progressPct} className="h-3" />

              {/* Key metrics grid */}
              <SimpleMetricGrid columns={2}>
                <SimpleMetric emoji="🐔" value={batch.current_bird_count?.toLocaleString() ?? "—"} size="md" />
                <SimpleMetric
                  emoji="📊"
                  value={batch.current_fcr != null ? Number(batch.current_fcr).toFixed(2) : "—"}
                  size="md"
                  color={batch.current_fcr != null ? getFcrColor(Number(batch.current_fcr)) : 'default'}
                />
                <SimpleMetric
                  emoji="💀"
                  value={mortPct.toFixed(1) + "%"}
                  size="md"
                  color={mortPct > 5 ? 'destructive' : mortPct > 3 ? 'warning' : 'success'}
                />
                <SimpleMetric
                  emoji="⚖️"
                  value={batch.latest_avg_weight_kg != null ? Number(batch.latest_avg_weight_kg).toFixed(2) : "—"}
                  unit="kg"
                  size="md"
                />
              </SimpleMetricGrid>
            </Card>
          </motion.div>
        ) : (
          /* ── NORMAL MODE HEADER ────────────────────── */
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bird className="w-5 h-5 text-primary" />
                  <h2 className="font-bold text-lg text-foreground">{batch.batch_code || "Batch"}</h2>
                </div>
                <Badge variant={statusVariant[batch.batch_status] ?? "secondary"}>
                  {t(`cbs.batch.status.${batch.batch_status}`, { defaultValue: batch.batch_status })}
                </Badge>
              </div>

              <p className="text-sm text-muted-foreground">{breedName}</p>

              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="font-semibold text-foreground">
                    {t("cbs.batchDetail.day", { defaultValue: "Day" })} {Math.min(currentDay, batch.planned_cycle_days)} <span className="font-normal text-muted-foreground">{t("cbs.batchDetail.of", { defaultValue: "of" })} {batch.planned_cycle_days}</span>
                  </span>
                  <span className="text-muted-foreground">{Math.round(progressPct)}%</span>
                </div>
                <Progress value={progressPct} className="h-2" />
              </div>

              <div className="grid grid-cols-4 gap-2 text-center pt-2">
                {[
                  { label: t("cbs.batchDetail.birds", { defaultValue: "Birds" }), value: batch.current_bird_count?.toLocaleString() ?? "—" },
                  { label: t("cbs.batch.fcr"), value: batch.current_fcr != null ? Number(batch.current_fcr).toFixed(2) : "—" },
                  { label: t("cbs.batchDetail.mortPercent", { defaultValue: "Mort %" }), value: mortPct.toFixed(1) + "%" },
                  { label: t("cbs.batchDetail.avgWt", { defaultValue: "Avg Wt" }), value: batch.latest_avg_weight_kg != null ? Number(batch.latest_avg_weight_kg).toFixed(2) + " kg" : "—" },
                ].map(s => (
                  <div key={s.label} className="rounded-lg bg-muted/50 p-2">
                    <div className="text-[10px] text-muted-foreground">{s.label}</div>
                    <div className="text-sm font-bold text-foreground">{s.value}</div>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {/* ── SIMPLE MODE: Recent records as emoji list ── */}
        {isSimpleMode && dailyRecords.length > 0 && (
          <Card className="p-4 space-y-3">
            <span className="text-lg font-semibold">📋</span>
            {[...dailyRecords].reverse().slice(0, 5).map(r => (
              <div key={r.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <span className="text-lg font-bold text-muted-foreground min-w-[40px]">D{r.batch_day}</span>
                <SimpleMetric emoji="🌾" value={r.feed_consumed_kg ?? "—"} unit="kg" size="sm" className="flex-1 p-1.5" />
                <SimpleMetric
                  emoji="💀"
                  value={r.mortality_count}
                  size="sm"
                  color={r.mortality_count > 0 ? 'destructive' : 'success'}
                  className="flex-1 p-1.5"
                />
              </div>
            ))}
          </Card>
        )}


        {/* ── Daily Records Table (normal mode only) ── */}
        {!isSimpleMode && (
          <>
            <SectionTitle icon={ClipboardList} title={t("cbs.batchDetail.dailyRecords", { defaultValue: "Daily Records" })} />
            <Card className="overflow-hidden">
              {dailyRecords.length > 0 ? (
                <div className="overflow-x-auto max-h-80">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/60 sticky top-0">
                      <tr>
                        {[t("cbs.batch.day"), t("common.date"), t("cbs.batchDetail.feedKg", { defaultValue: "Feed (kg)" }), t("cbs.batchDetail.mort", { defaultValue: "Mort" }), t("cbs.batchDetail.tempC", { defaultValue: "Temp (°C)" }), t("cbs.batchDetail.notes", { defaultValue: "Notes" })].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {[...dailyRecords].reverse().slice(0, 30).map(r => (
                        <tr key={r.id} className="hover:bg-muted/30">
                          <td className="px-3 py-2 font-semibold text-foreground">{r.batch_day}</td>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.record_date}</td>
                          <td className="px-3 py-2 text-foreground">{r.feed_consumed_kg ?? "—"}</td>
                          <td className={cn("px-3 py-2 font-medium", r.mortality_count > 0 ? "text-destructive" : "text-foreground")}>{r.mortality_count}</td>
                          <td className="px-3 py-2 text-foreground">
                            {r.temperature_min_c != null && r.temperature_max_c != null
                              ? `${r.temperature_min_c}–${r.temperature_max_c}`
                              : "—"}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground max-w-[120px] truncate">{"—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-6 text-center text-sm text-muted-foreground">{t("cbs.batchDetail.noRecords", { defaultValue: "No daily records yet" })}</div>
              )}
            </Card>
          </>
        )}

        {/* ── Vaccination Log ──────────────────────── */}
        {!isSimpleMode ? (
          <>
            <SectionTitle icon={Syringe} title={t("cbs.batchDetail.vaccinationLog", { defaultValue: "Vaccination Log" })} />
            <Card className="p-4">
              {vaccinations.length > 0 ? (
                <div className="space-y-3">
                  {vaccinations.map(v => (
                    <div key={v.id} className="flex items-start gap-3 border-b border-border pb-3 last:border-0 last:pb-0">
                      <div className="w-8 h-8 rounded-full bg-info/10 flex-center shrink-0">
                        <Syringe className="w-4 h-4 text-info" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{v.vaccine_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {v.vaccination_date}{v.administration_route ? ` · ${v.administration_route}` : ""}{v.batch_day ? ` · ${t("cbs.batchDetail.day", { defaultValue: "Day" })} ${v.batch_day}` : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-sm text-muted-foreground py-4">{t("cbs.batchDetail.noVaccinations", { defaultValue: "No vaccinations recorded" })}</div>
              )}
            </Card>
          </>
        ) : vaccinations.length > 0 && (
          <Card className="p-4 space-y-2">
            {vaccinations.map(v => (
              <SimpleMetric key={v.id} emoji="💉" value={v.vaccine_name} unit={v.vaccination_date} size="sm" />
            ))}
          </Card>
        )}


        {/* ── Settlement Preview ─────────────────── */}
        {showSettlement && (
          <>
            <SectionTitle icon={DollarSign} title={t("cbs.batchDetail.settlementPreview", { defaultValue: "Settlement Preview" })} />
            <Card className="p-5">
              {settlement ? (
                <div className="space-y-3">
                  {isSimpleMode ? (
                    <SimpleMetric emoji="💰" value={settlement.netAmount?.toLocaleString() ?? "—"} unit={settlement.currency} size="lg" color="success" />
                  ) : (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t("cbs.batchDetail.grossAmount", { defaultValue: "Gross Amount" })}</span>
                        <span className="font-semibold text-foreground">{settlement.currency} {settlement.grossAmount?.toLocaleString()}</span>
                      </div>
                      {settlement.deductions && Object.entries(settlement.deductions).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t(`cbs.settlement.deductions.${k}`, { defaultValue: k.replace(/_/g, " ") })}</span>
                          <span className="text-destructive">-{(v as number).toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="border-t border-border pt-2 flex justify-between">
                        <span className="font-bold text-foreground">{t("cbs.batchDetail.netAmount", { defaultValue: "Net Amount" })}</span>
                        <span className="font-bold text-primary text-lg">{settlement.currency} {settlement.netAmount?.toLocaleString()}</span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center text-sm text-muted-foreground py-4">{t("cbs.batchDetail.settlementUnavailable", { defaultValue: "Settlement data unavailable" })}</div>
              )}
              {settlement?.settlementId && (
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full mt-3 gap-2"
                  onClick={() => navigate(`/settlement/${settlement.settlementId}`)}
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('cbs.settlement.viewSettlement')}
                </Button>
              )}
            </Card>
          </>
        )}
      </div>
    </Screen>
  );
};

// ── Helpers ────────────────────────────────────────────

const SectionTitle: React.FC<{ icon: React.ElementType; title: string }> = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 pt-2">
    <Icon className="w-4 h-4 text-primary" />
    <h3 className="font-semibold text-foreground text-sm">{title}</h3>
  </div>
);

